import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Converts Title Case, CamelCase, UPPERCASE, and snake_case labels into natural Sentence case.
 * Examples:
 * - "Account Type" -> "Account type"
 * - "Company Name" -> "Company name"
 * - "GST Number" -> "GST number"
 * - "PAN Number" -> "PAN number"
 * - "IFSC Code" -> "IFSC code"
 * - "Alternative Phone Number" -> "Alternative phone number"
 */
export function formatToSentenceCase(text: string): string {
  if (!text || typeof text !== 'string') return text || '';

  // 1. Remove accidental '(optional)' or '(Optional)'
  let cleaned = text.replace(/\s*\(\s*optional\s*\)\s*/gi, '').trim();
  if (!cleaned) return '';

  // If text contains '/', format each segment independently so words after '/' start with uppercase
  if (cleaned.includes('/')) {
    return cleaned
      .split('/')
      .map(part => formatToSentenceCase(part.trim()))
      .join(' / ');
  }

  // 2. Separate camelCase or PascalCase words (e.g. "companyName" -> "company Name")
  cleaned = cleaned.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  // 3. Separate underscores or hyphens if any
  cleaned = cleaned.replace(/[_-]+/g, ' ');

  // 4. Split into words
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';

  // Common acronyms to preserve in UPPERCASE
  const acronyms = new Set([
    'FY', 'ID', 'PAN', 'GST', 'GSTIN', 'URL', 'API', 'OTP', 'PO', 'PR',
    'CVV', 'SSN', 'HRMS', 'TDS', 'TCS', 'CIN', 'LLPIN', 'IEC', 'IFSC',
    'MICR', 'SWIFT', 'NEFT', 'RTGS', 'IMPS', 'UPI', 'SKU', 'HSN', 'SAC',
    'PDF', 'CSV', 'MSME', 'TAN', 'VAT', 'CST', 'SEZ', 'LUT', 'DSO', 'UTR'
  ]);

  return words
    .map((word, index) => {
      const cleanWord = word.replace(/[^a-zA-Z0-9]/g, '');
      if (acronyms.has(cleanWord.toUpperCase())) {
        return word.replace(new RegExp(cleanWord, 'i'), cleanWord.toUpperCase());
      }
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word.toLowerCase();
    })
    .join(' ');

}

@Component({
  selector: 'app-label',
  templateUrl: './app-label.component.html',
  styleUrls: ['./app-label.component.scss'],
})
export class AppLabelComponent implements OnInit, OnChanges {
  @Input() text: string = '';
  @Input() mandatory: boolean = false;
  @Input() set required(val: boolean) {
    this.mandatory = val;
  }
  get required(): boolean {
    return this.mandatory;
  }
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() icon: string = '';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() forId: string = '';
  @Input() customClass: string = '';

  cleanText: string = '';

  ngOnInit(): void {
    this.updateCleanText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text']) {
      this.updateCleanText();
    }
  }

  private updateCleanText(): void {
    this.cleanText = formatToSentenceCase(this.text);
  }
}
